import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import {
  updateEmployee,
  selectEmployeesStatus,
  setEditingEmployment,
  updateEditingEmployment,
  clearEditingEmployment,
  selectEditingEmployment,
} from '../../slices/employees/employeesSlice';
import { Employee, EmployeeEmployments } from '../../models/employee.model';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

export function useEmploymentCardForm(employee: Partial<Employee>) {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [showAddSocialModal, setAddSocialShowModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);

  // Get editing state from Redux
  const editingEmployment = useAppSelector(
    selectEditingEmployment
  ) as Partial<EmployeeEmployments> | null;
  const status = useAppSelector(selectEmployeesStatus);

  // Check if this employment is being edited
  const currentEmployment = employee.employments?.[0];
  const isEdited = !!editingEmployment && editingEmployment.employee_id === employee.id;

  // Initialize the editing employment in Redux if needed
  useEffect(() => {
    if (employee && currentEmployment && !editingEmployment && isEdited) {
      dispatch(
        setEditingEmployment({
          ...currentEmployment,
          employee_id: employee.id,
        })
      );
    }
  }, [employee, currentEmployment, editingEmployment, dispatch, isEdited]);

  const handleChange =
    <K extends keyof EmployeeEmployments>(field: K) =>
    (value: any) => {
      if (isEdited) {
        dispatch(updateEditingEmployment({ field, value }));
      }
    };

  const saveChanges = async () => {
    if (isEdited && editingEmployment && employee.id) {
      dispatch(showOverlay());
      dispatch(
        updateEmployee({
          employeeId: employee.id,
          employmentId: currentEmployment?.id,
          employmentData: editingEmployment,
        })
      )
        .unwrap()
        .finally(() => dispatch(hideOverlay()));
      setShowModal(false);
    }
  };

  const cancelChanges = () => {
    dispatch(clearEditingEmployment());
  };

  const toggleEditing = () => {
    if (isEdited) {
      dispatch(clearEditingEmployment());
    } else if (currentEmployment) {
      dispatch(
        setEditingEmployment({
          ...currentEmployment,
          employee_id: employee.id,
        })
      );
    } else {
      dispatch(
        setEditingEmployment({
          position_title: '',
          position_description: '',
          position_type: '',
          employee_identification_number: null,
          department: '',
          start_date: '',
          end_date: '',
          employee_id: employee.id,
          employee_tax_number: '',
        } as Partial<EmployeeEmployments>)
      );
    }
  };

  const toogleEditingSocialModal = () => {
    setAddSocialShowModal((prev) => !prev);
  };

  const confirmExit = () => {
    dispatch(clearEditingEmployment());
    setShowModal(false);
  };

  // Handle clicks outside the form
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEdited && formRef.current && !formRef.current.contains(event.target as Node)) {
        const targetElement = event.target as HTMLElement;
        if (
          targetElement.closest('.MuiDatePicker-root') ||
          targetElement.closest('.MuiPickersPopper-root') ||
          targetElement.closest('.MuiLocalizationProvider-root') ||
          targetElement.closest('.MuiDialog-root')
        ) {
          return;
        }

        setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEdited]);

  return {
    formData: editingEmployment || currentEmployment || {},
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    setShowModal,
    confirmExit,
    showAddSocialModal,
    setAddSocialShowModal,
    toogleEditingSocialModal,
    isLoading: status === 'loading',
    cancelChanges,
  };
}
